import React from 'react'
import PropTypes from 'prop-types'
import * as request from 'services/request'
import notify from 'utils/notification'
import Auth from 'services/auth/auth-service'
import MyExperiencesComponent from './my-experiences'

class MyExperiencesContainer extends React.Component {
  constructor (props) {
    super(props)
    this.state = {
      experiences: [],
      search: '',
      statusFilter: 'active',
      participantsLoaded: false
    }

    this.handleSearch = this.handleSearch.bind(this)
    this.handleFilter = this.handleFilter.bind(this)
    this.handleStatusUpdate = this.handleStatusUpdate.bind(this)
    this.getParticipants = this.getParticipants.bind(this)
  }

  componentDidMount () {
    this.getExperiences()
  }

  componentDidUpdate (prevProps, prevState, prevContext) {
    const { tenant: newTenant, user: newUser } = this.context
    const { tenant: prevTenant, user: prevUser } = prevContext

    if (Object.is(newTenant, prevTenant) && Object.is(newUser, prevUser)) {
      // selected user and tenant didn't change, no need to refresh experiences
      return
    }

    // update experiences
    this.getExperiences()
  }

  async getParticipants () {
    const { experiences } = this.state

    if (!experiences || !experiences.length) return

    const experiencesWithParticipants = await Promise.all(
      experiences.map(async xp => {
        const response = await request.authorized(`/participants/${xp.id}`)
        const participants = await response.json()
        return { ...xp, participants }
      })
    )

    this.setState({
      ...this.state,
      experiences: experiencesWithParticipants,
      participantsLoaded: true
    })
  }

  async getExperiences () {
    const { user, tenant } = this.context
    let experiences

    if (!user && !tenant) {
      // get current user experiences
      const userId = Auth.id // current user id
      const response = await request.authorized(`/users/${userId}/widgets`)
      experiences = await response.json()
    } else if (user && tenant) {
      // get specific tenant user experiences
      const response = await request.authorized(
        `/tenants/${tenant.id}/${user.id}/widgets`
      )
      experiences = await response.json()
    } else if (user) {
      // get specific user experiences
      const response = await request.authorized(`/users/${user.id}/widgets`)
      experiences = await response.json()
    } else {
      // get specific tenant experiences
      const response = await request.authorized(`/tenants/${tenant.id}/widgets`)
      experiences = await response.json()
    }

    this.setState({
      experiences: experiences.map(xp => ({ ...xp, participants: [] }))
    })
  }

  getFilteredExperiences () {
    const { experiences, search, statusFilter } = this.state

    // status filter
    let filteredExperiences =
      statusFilter !== 'all'
        ? experiences.filter(
            xp => xp.widgetData.status.toLowerCase() === statusFilter
          )
        : experiences

    // name or type search
    filteredExperiences = search
      ? filteredExperiences.filter(xp => {
        const { name, type } = xp.widgetData
        const searchLowerCase = search.toLowerCase()

        return (
            name.toLowerCase().includes(searchLowerCase) ||
            type.toLowerCase().includes(searchLowerCase)
        )
      })
      : filteredExperiences

    return filteredExperiences
  }

  getActiveExperienceCount () {
    const { experiences } = this.state
    return experiences.filter(
      xp => xp.widgetData.status.toLowerCase() === 'active'
    ).length
  }

  handleSearch ({ target }) {
    this.setState({ search: target.value })
  }

  handleFilter ({ target }) {
    this.setState({ statusFilter: target.value })
  }

  async handleStatusUpdate ({ id, status }) {
    let updatedStatus
    try {
      const response = await request.authorized(`/widgets/${id}/status`, {
        method: 'PUT',
        body: { status }
      })

      updatedStatus = await response.text()
    } catch (err) {
      if (err.message === '422') {
        // user tried to activate experience that has no owner
        return notify.error({
          message: 'Cannot activate experience without an assigned owner'
        })
      }

      if (err.message === '502') {
        // rule manager is currently forbidding updates
        return notify.error({
          message:
            'Cannot update experience at the moment, please try again later'
        })
      }

      return notify.error({
        title: 'Failed to update experience',
        message: 'Please contact your administrator if the problem persists'
      })
    }

    // update local status of experience in state
    const { experiences } = this.state
    let experienceName
    const updatedExperiences = experiences.map(xp => {
      if (id === xp.id) {
        experienceName = xp.widgetData.name
        return {
          ...xp,
          widgetData: {
            ...xp.widgetData,
            status: updatedStatus
          }
        }
      }
      return xp
    })

    this.setState({ experiences: updatedExperiences })
    notify.success({
      message: `Experience '${experienceName}' status set to '${updatedStatus}'`
    })
  }

  render () {
    return (
      <MyExperiencesComponent
        onSearch={this.handleSearch}
        onFilter={this.handleFilter}
        onStatusUpdate={this.handleStatusUpdate}
        experiences={this.getFilteredExperiences()}
        activeExperienceCount={this.getActiveExperienceCount()}
        canCreate={Auth.canCreateWidgets}
        getParticipants={this.getParticipants}
        participantsLoaded={this.state.participantsLoaded}
      />
    )
  }
}

MyExperiencesContainer.contextTypes = {
  tenant: PropTypes.object,
  user: PropTypes.object
}

export default MyExperiencesContainer



// WEBPACK FOOTER //
// ./src/pages/my-experiences/index.js